import { monthOf, shiftMonth } from '@d-orca/shared/client/activities';
import { entryStatusId } from '@d-orca/shared/client/logistics';
import type { Delivery, DeliveryBoardStatus } from '@interfaces/ILogistics';
import { isOverdue, isOverdueOn, overdueDays } from '../board.logic';
import { addDays, zonedDay } from '../logistics.format';

export type CalendarMode = 'month' | 'week';

export const DAYS_PER_WEEK = 7;

/** Which day an order sits on: the day it landed, else the day it is due. Null
 *  is an order nobody has dated yet. */
export function calendarDay(delivery: Delivery, tz: string): string | null {
  return delivery.deliveredAt ? zonedDay(delivery.deliveredAt, tz) : delivery.dueDate;
}

export const SYNCED_LEGEND_KEY = 'synced';
/** Not a column: any order past its due date, whatever column it is in. */
export const OVERDUE_LEGEND_KEY = 'overdue';

/** What the legend files an order under: synced history, else its column. A
 *  delivered order not synced yet files under the Delivered column's id; one
 *  naming a column the site deleted, under the entry column, where the board
 *  puts it and whose color it wears. */
export function legendKey(delivery: Delivery, statuses: DeliveryBoardStatus[]): string {
  if (delivery.isSynced) return SYNCED_LEGEND_KEY;
  const known = statuses.some((status) => status.id === delivery.status);
  return known ? delivery.status : (entryStatusId(statuses) ?? delivery.status);
}

/** The orders under any picked legend entry on the site's `today`; every order while none is picked. */
export function filterByLegend(
  deliveries: Delivery[],
  picked: ReadonlySet<string>,
  statuses: DeliveryBoardStatus[],
  today: string,
): Delivery[] {
  if (picked.size === 0) return deliveries;
  const overdue = picked.has(OVERDUE_LEGEND_KEY);
  return deliveries.filter(
    (delivery) => picked.has(legendKey(delivery, statuses)) || (overdue && isOverdueOn(delivery, today)),
  );
}

/** The Sunday opening the week `day` falls in. */
export function weekStart(day: string): string {
  return addDays(day, -new Date(`${day}T00:00:00Z`).getUTCDay());
}

/** Every day the grid shows: the one week, or each whole week holding a day of
 *  the month. */
export function gridDays(mode: CalendarMode, anchor: string): string[] {
  const month = monthOf(anchor);
  const first = weekStart(mode === 'month' ? month : anchor);
  const end = mode === 'month' ? shiftMonth(month, 1) : addDays(first, DAYS_PER_WEEK);
  const days: string[] = [];
  for (let day = first; day < end || days.length % DAYS_PER_WEEK !== 0; day = addDays(day, 1)) days.push(day);
  return days;
}

/** The anchor one page back or forward. */
export function stepAnchor(mode: CalendarMode, anchor: string, by: number): string {
  return mode === 'month' ? shiftMonth(monthOf(anchor), by) : addDays(anchor, by * DAYS_PER_WEEK);
}

/** Each day's orders, the ones needing attention first: critical, then late.
 *  An order sits on its due date until it lands, so one still open on a past
 *  day is late. */
export function ordersByDay(deliveries: Delivery[], today: string, tz: string): Map<string, Delivery[]> {
  const byDay = new Map<string, Delivery[]>();
  for (const delivery of deliveries) {
    const day = calendarDay(delivery, tz);
    if (!day) continue;
    const orders = byDay.get(day);
    if (orders) orders.push(delivery);
    else byDay.set(day, [delivery]);
  }
  for (const [day, orders] of byDay) {
    const isLate = (delivery: Delivery) => Number(day < today && !delivery.deliveredAt);
    orders.sort((a, b) => Number(b.isCritical) - Number(a.isCritical) || isLate(b) - isLate(a));
  }
  return byDay;
}

/** The late orders, longest overdue first. */
export function overdueOrders(deliveries: Delivery[], now: Date, tz: string): Delivery[] {
  return deliveries
    .filter((delivery) => isOverdue(delivery, now, tz))
    .sort((a, b) => overdueDays(b, now, tz) - overdueDays(a, now, tz));
}

/** The orders with no day to sit on. */
export function undatedOrders(deliveries: Delivery[], tz: string): Delivery[] {
  return deliveries.filter((delivery) => calendarDay(delivery, tz) === null);
}

/* A month cell's layout, as CalendarDayCell.module.css and CalendarChip.module.css
   draw it, rounded up so a fit never overflows the cell. */
const CELL_FRAME = 11; // 6px top + 4px bottom padding, 1px bottom border
const DAY_NUMBER = 21;
// A chip, or "+N more", with the 3px gap above it.
const CELL_ROW = 21 + 3;

/** How many chip rows a month cell this tall holds under its day number. */
export function monthCellRows(cellHeight: number): number {
  return Math.floor((cellHeight - CELL_FRAME - DAY_NUMBER) / CELL_ROW);
}

/** How many of a day's orders its cell spells out: all of them when they fit,
 *  else all but a row kept for "+N more" (always at least one). */
export function monthCellFit(rows: number, count: number): number {
  return count <= rows ? count : Math.max(1, rows - 1);
}
